import React, { useState, useEffect } from 'react';
import PageLayout from '../../../components/PageLayout/CarelonRx';
import { useFlowNavigation, useFlow } from '../../../contexts/FlowContext';
import SessionExpiryModal from '../../../components/modals/SessionExpiryModal';
import SecureSessionService from '../../../services/session/SecureSessionService';
import RateLimiter from '../../../security/RateLimiter';
import MfaService from '../../../services/mfa/MFAService';
import { VerifyOtpRequest, InitiateOtpRequest, FactorType } from '../../../types/mfa';
import '../../../styles/CarelonRx/login.css';
import { ORIGINAL_CLIENT_REDIRECT_URL } from '../../../config';
import { getLogoutRedirectUrl } from '../../../utils/nativeApp';
import '../../../styles/CarelonRx/mfa.css';
import '../../../styles/CarelonRx/modal.css';

const SecurityVerificationPage: React.FC = () => {
    const { previousStep, nextStep, goToStep } = useFlowNavigation();
    const { getFlowData, setFlowData, completeStep, currentStep, resetFlow } = useFlow();
    const isNativeApp = getFlowData('device') === 'native_app';
    const [verificationCode, setVerificationCode] = useState('');
    const [error, setError] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [isRequestingNewCode, setIsRequestingNewCode] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(30);
    const [canRequestNew, setCanRequestNew] = useState(false);
    const [fieldTouched, setFieldTouched] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(true);
    const [timerKey, setTimerKey] = useState(0);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showAccountLockedModal, setShowAccountLockedModal] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [supportNumber, setSupportNumber] = useState<string>('844-430-0335');
    const [supportNumberError, setSupportNumberError] = useState<string>('');
    const [isApiError, setIsApiError] = useState(false);

    const mfaContactValue = getFlowData('mfaContactValue');
    const mfaMethod = getFlowData('mfaMethod');

    useEffect(() => {
        
        const storedSession = SecureSessionService.getStoredSession();
        if (storedSession?.expiresAt) {
            window.dispatchEvent(new CustomEvent('session-refreshed', { detail: { expiresAt: storedSession.expiresAt } }));
        }

        const supportContact = getFlowData('supportContact');
        if (supportContact) {
            setSupportNumber(supportContact);
            setSupportNumberError('');
        } else {
            setSupportNumberError("If your contact details are incorrect, please call the Pharmacy Member Services number on your member ID card. If you don't have your ID card, please check back later. We are currently experiencing an issue displaying the number and are working to fix it. Pharmacy Member Services is available 24/7.");
        }
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev <= 1) {
                    setCanRequestNew(true);
                    setShowSuccessMessage(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timerKey]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}s`;
    };


    const handleCancel = () => {
        setShowCancelModal(true);
    };

    const confirmCancel = () => {
        sessionStorage.removeItem('flowState');
        sessionStorage.clear();
        SecureSessionService.clearSession();
        const redirectUrl = isNativeApp
            ? getLogoutRedirectUrl()
            : ORIGINAL_CLIENT_REDIRECT_URL;
        window.location.replace(redirectUrl);
    };

    const dismissCancelModal = () => {
        setShowCancelModal(false);
    };

    const handleVerify = async () => {
        if (!verificationCode.trim()) {
            setError('Please enter the verification code');
            return;
        }

        if (verificationCode.length < 4) {
            setError('Verification code must be at least 4 characters');
            return;
        }

        const session = SecureSessionService.getStoredSession();
        if (!session) {
            setError('Session expired. Please log in again.');
            return;
        }

        const userIdentifier = session?.userId || mfaContactValue || 'anonymous';
        const rateLimitResult = RateLimiter.isAllowed(`otp_verify_${userIdentifier}`, 5, 300000);
        
        if (!rateLimitResult.allowed) {
            setError('Too many attempts. Please wait 5 minutes before trying again.');
            setIsApiError(false);
            return;
        }

        const factorId = getFlowData('factorId');
        const isFactorNewlyEnrolled = getFlowData('isFactorNewlyEnrolled');

        if (!factorId) {
            setError('Missing required information. Please try sending the code again.');
            return;
        }

        try {
            setIsVerifying(true);
            setError('');

            const request: VerifyOtpRequest = {
                factorId,
                passCode: verificationCode,
                isFactorNewlyEnrolled: isFactorNewlyEnrolled || false
            };
            const result = await MfaService.verifyOtp(request, session.sessionId);
        
            if (result.success) {
                setFlowData('verificationCode', verificationCode);
                if (currentStep) {
                    completeStep(currentStep.id);
                }
                nextStep();
            } else {
                const errorMessage = result.message || result.error || 'Verification failed. Please try again.';
                
                if (errorMessage.includes('User Locked')) {
                    setShowAccountLockedModal(true);
                    return;
                }
                
                setError(errorMessage);
                setIsApiError(false);
                setFieldTouched(false);
                setShowSuccessMessage(false);
            }
        } catch (err: any) {
            const statusCode = err?.response?.status;
            const backendMessage = err?.response?.data?.message || err?.response?.data?.error || '';

            if (backendMessage.includes('User Locked')) {
                setShowAccountLockedModal(true);
                return;
            }

            let isApiErrorFlag = true;
            
            if (statusCode === 400 && (backendMessage.includes('Invalid Passcode') || backendMessage.includes('Code Expired'))) {
                isApiErrorFlag = false;
            } else if (statusCode === 401 || statusCode === 500 || statusCode >= 500) {
                isApiErrorFlag = true;
            } else {
                isApiErrorFlag = false;
            }

            setError(backendMessage);
            setIsApiError(isApiErrorFlag);
            setFieldTouched(false);
            setShowSuccessMessage(false);
        } finally {
            setIsVerifying(false);
        }
    };

    const handleRequestNewCode = async () => {
        try {
            setIsRequestingNewCode(true);
            setError('');
            setIsApiError(false);
            setFieldTouched(false);
            setShowSuccessMessage(false);

            const session = SecureSessionService.getStoredSession();
            if (!session) {
                setError('Session expired. Please log in again.');
                setIsRequestingNewCode(false);
                return;
            }

            const factorType = getFlowData('factorType') as FactorType;
            const selectedContact = getFlowData('selectedContact');

            if (!factorType || !selectedContact) {
                setError('Missing required information. Please try again.');
                setIsRequestingNewCode(false);
                return;
            }

            const request: InitiateOtpRequest = {
                factorType,
                contact: factorType === 'EMAIL' 
                    ? { email: selectedContact }
                    : { phoneNumber: selectedContact }
            };

            const result = await MfaService.initiateOtp(request, session.sessionId);

            if (result.success) {
                setFlowData('factorId', result.factorId);
                setFlowData('isFactorNewlyEnrolled', result.isFactorNewlyEnrolled);
                
                setVerificationCode('');
                setTimeRemaining(30);
                setCanRequestNew(false);
                setTimerKey(prev => prev + 1);
                setShowSuccessMessage(true);
            } else {
                setError('API Error');
                setIsApiError(true);
            }
        } catch (err: any) {
            setError('API Error');
            setIsApiError(true);
            setFieldTouched(false);
            setShowSuccessMessage(false);
        } finally {
            setIsRequestingNewCode(false);
        }
    };

    return (
        <PageLayout bannerTitle="Security Verification">
            <div className="login-page-container">
                <div className="login-left-panel">

                    <h1 className="mfa-title">Enter the verification code.</h1>

                    <p className="mfa-subtitle">
                       Your verification code was sent to <span className="mfa-contact-value">{mfaContactValue}</span>.
                    </p>

                    <div className="mfa-verification-form">
                        <div className="mfa-form-field">
                            <label htmlFor="verification-code" className={`mfa-form-label ${(fieldTouched && verificationCode.length > 0 && verificationCode.length <= 4) || (fieldTouched && !verificationCode.trim()) ? 'error' : ''}`}>
                                Verification Code
                            </label>
                            <div className="mfa-input-wrapper">
                                <input
                                    id="verification-code"
                                    type="text"
                                    value={verificationCode}
                                    onChange={(e) => {
                                        setVerificationCode(e.target.value);
                                        if (fieldTouched && e.target.value.trim() === '') {
                                            setError('Required field.');
                                        } else {
                                            setError('');
                                        }
                                    }}
                                    onBlur={() => {
                                        setFieldTouched(true);
                                        if (!verificationCode.trim()) {
                                            setError('Required field.');
                                        }
                                    }}
                                    placeholder="Enter verification code"
                                    className={`crx-input ${(fieldTouched && verificationCode.length > 0 && verificationCode.length <= 4) || (fieldTouched && !verificationCode.trim()) ? 'error' : ''} ${verificationCode.length > 4 ? 'valid' : ''}`}
                                    disabled={isVerifying}
                                />
                            </div>
                            {(fieldTouched && !verificationCode.trim()) && (
                                <div className="crx-error-container">
                                    <img src="/assets/carelonRx/images/info_error.svg" alt="" className="error-icon" />
                                    <span className="crx-error-text">Required field.</span>
                                </div>
                            )}
                            <div className="mfa-code-actions">
                                <span
                                    className={`mfa-request-code-link ${(!canRequestNew || isRequestingNewCode) ? 'disabled' : ''}`}
                                    onClick={() => {
                                        if (canRequestNew && !isRequestingNewCode) {
                                            handleRequestNewCode();
                                        }
                                    }}
                                >
                                    Request New Verification Code
                                </span>

                                {timeRemaining > 0 && (
                                    <span className="mfa-timer">
                                        {formatTime(timeRemaining)}
                                    </span>
                                )}
                            </div>

                            {error && !fieldTouched && (
                                <div className="mfa-otp-error-container">
                                    {isApiError ? (
                                        <p>Sorry, we are not able to send you a verification code. We are experiencing a technical issue. Please try again soon.</p>
                                    ) : (
                                        <p>The code you entered may be incorrect or has expired. Please try entering it again or request a new code.</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {showSuccessMessage && (
                            <div className="mfa-success-message">
                                Your verification code has been sent and is valid for only five minutes.
                            </div>
                        )}
                    </div>

                    <div className="mfa-footer">
                        <div className="mfa-footer-buttons">
                            <button onClick={handleCancel} className="crx-button crx-button-secondary">
                                Cancel
                            </button>
                            <button
                                onClick={handleVerify}
                                disabled={verificationCode.length <= 4 || isVerifying}
                                className="crx-button crx-button-primary"
                            >
                                {isVerifying ? 'Verifying...' : 'Log In'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showCancelModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2 className="modal-title">Are you sure you want to cancel?</h2>
                        <p className="modal-message">
                            Doing so will prevent you from logging in.
                        </p>
                        <div className="modal-actions">
                            <button onClick={dismissCancelModal} className="modal-btn-cancel">
                                No, Don't Cancel
                            </button>
                            <button onClick={confirmCancel} className="modal-btn-stay">
                                Yes, Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showAccountLockedModal && (
                <div className="modal-overlay">
                    <div className="modal-content account-locked-modal-content">
                        <h2 className="modal-title account-locked-modal-title">
                            Your account is currently locked for security reasons.
                        </h2>
                        <div className="account-locked-modal-message">
                            <p className="modal-message">
                                To regain access, please contact our Technical Support team so they can verify your information and unlock your account.
                                They can be reached at  <a href="tel:1-844-430-0335" className="mfa-contact-phone-link">1-844-430-0335</a> (TTY: 711), Monday - Friday, 8 a.m. to 8 p.m. ET.
                            </p>
                        </div>
                        <div className="modal-actions account-locked-modal-actions">
                            <button 
                                onClick={() => {
                                    setShowAccountLockedModal(false);
                                    SecureSessionService.clearSession();
                                    const redirectUrl = isNativeApp
                                        ? getLogoutRedirectUrl()
                                        : ORIGINAL_CLIENT_REDIRECT_URL;
                                    console.log('[SecurityVerificationPage] accountLocked redirecting to:', redirectUrl);
                                    window.location.replace(redirectUrl);
                                }} 
                                className="modal-btn-cancel account-locked-modal-button"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <SessionExpiryModal />
        </PageLayout>
    );
};

export default SecurityVerificationPage;
